export interface LaunchComposerDraft {
  body: string
  toAddress: string
}

/**
 * Session-scoped store of unsent composer drafts, keyed by brand id.
 *
 * A launch specialist works many brand conversations at once; switching brands
 * must not discard a half-written reply. The store is module-level so a draft
 * survives switching away and back (and the composer remounting) within the
 * session. It is intentionally in-memory — drafts are transient and need not
 * persist across full page reloads.
 */
const draftsByBrandId = new Map<string, LaunchComposerDraft>()

/**
 *
 * @param draft
 */
function hasContent(draft: LaunchComposerDraft): boolean {
  return draft.body.trim().length > 0 || draft.toAddress.trim().length > 0
}

/**
 *
 * @param brandId
 */
export function getLaunchComposerDraft(
  brandId: string | null
): LaunchComposerDraft | null {
  if (!brandId) return null
  return draftsByBrandId.get(brandId) ?? null
}

/**
 * Saves the draft for a brand, or clears it when the draft is empty so the
 * store does not accumulate blank entries.
 * @param brandId
 * @param draft
 */
export function setLaunchComposerDraft(
  brandId: string | null,
  draft: LaunchComposerDraft
): void {
  if (!brandId) return

  if (hasContent(draft)) {
    draftsByBrandId.set(brandId, draft)
  } else {
    draftsByBrandId.delete(brandId)
  }
}

/**
 *
 * @param brandId
 */
export function clearLaunchComposerDraft(brandId: string | null): void {
  if (!brandId) return
  draftsByBrandId.delete(brandId)
}

/**
 * Drops every stored draft. The store is module-level and survives client-side
 * navigation, so the logout handler must call this — otherwise a second
 * specialist signing in without a full page reload would inherit the previous
 * user's unsent draft text in any shared-brand composer.
 */
export function clearAllLaunchComposerDrafts(): void {
  draftsByBrandId.clear()
}
